import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons, Feather } from '@expo/vector-icons';
import { Paprika, PaprikaRadius } from '../../styles/theme';
import { useTranslation } from 'react-i18next';

interface Props {
  remainingSec: number;
  running: boolean;
  big?: boolean;
  onToggle: () => void;
  onReset: () => void;
}

function formatTimerTime(seconds: number): string {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/**
 * Per-step timer card (handoff `TimerCard`). Rendered only for steps with a
 * parsed duration; paused by default — the user starts it.
 */
export const TimerCard: React.FC<Props> = ({ remainingSec, running, big = false, onToggle, onReset }) => {
  const { t } = useTranslation();
  const done = remainingSec === 0;
  return (
    <View style={[styles.card, big && styles.cardBig]}>
      <Ionicons name="timer-outline" size={big ? 22 : 19} color={Paprika.accent} />
      <View style={styles.timeCol}>
        <Text style={styles.label}>{t('cooking.timer')}</Text>
        <Text style={[styles.time, big && styles.timeBig]}>
          {done ? t('cooking.timerDone') : formatTimerTime(remainingSec)}
        </Text>
      </View>
      <TouchableOpacity style={[styles.toggleBtn, big && styles.toggleBtnBig]} onPress={onToggle}>
        <Ionicons name={running ? 'pause' : 'play'} size={15} color={Paprika.onPrimary} />
        <Text style={styles.toggleText}>{running ? t('cooking.timerPause') : t('cooking.timerStart')}</Text>
      </TouchableOpacity>
      <TouchableOpacity style={[styles.resetBtn, big && styles.resetBtnBig]} onPress={onReset}>
        <Feather name="rotate-ccw" size={16} color={Paprika.accent} />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    paddingVertical: 11,
    paddingLeft: 16,
    paddingRight: 12,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
  },
  cardBig: {
    gap: 16,
    paddingVertical: 14,
    paddingLeft: 20,
    paddingRight: 16,
    maxWidth: 380,
  },
  timeCol: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  time: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 26,
    lineHeight: 30,
    color: Paprika.brandDeep,
    marginTop: 1,
    fontVariant: ['tabular-nums'],
  },
  timeBig: {
    fontSize: 34,
    lineHeight: 38,
  },
  toggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 38,
    paddingHorizontal: 15,
    borderRadius: 11,
    backgroundColor: Paprika.accent,
  },
  toggleBtnBig: {
    height: 44,
  },
  toggleText: {
    fontSize: 13,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.onPrimary,
  },
  resetBtn: {
    width: 38,
    height: 38,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
  },
  resetBtnBig: {
    width: 44,
    height: 44,
  },
});
